import { FC, useCallback, useEffect, useState } from "react";
import styles from "./Profile.module.scss";
import { useAppDispatch, useAppSelector } from "../../services/hooks";
import { Button } from "../../components";
import { IInputGeneral, inputGeneral, TypeIdGeneral } from "./constants";
import { IRequestPatchUser } from "../../types/user";
import { fetchUser, updateUser } from "../../services/slices/user";
import { Input } from "../../components/ui/Input/Input";
import { InputPassword } from "../../components/ui/InputPassword/InputPassword";
import { formatDate } from "../../utils/utils";
import Loader from "../../components/Loader/Loader";
import { MenuTabs } from "../../components/MenuTabs/MenuTabs";
import { useLocation } from "react-router-dom";
import { Chat } from "../../components/Chat/Chat";
import AvatarCircle from "../../components/AvatarCircle/AvatarCircle";
import { ErrorPage } from "../ErrorPage/ErrorPage";
import { deleteAvatarClient, deletePhoto } from "../../api/user";

export const Profile: FC = () => {
  const { user, loading } = useAppSelector((state) => state.user);
  const dispatch = useAppDispatch();
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [formData, setFormData] = useState<IInputGeneral[]>(inputGeneral);
  const router = useLocation();
  const [image, setImage] = useState<string | null>(
    user?.profileAvatar?.avatarUrl ? user?.profileAvatar?.avatarUrl : null,
  );
  const handleImageRemove = () => {
    setImage(null); // Очищаем изображение
  };
  const setUserData = useCallback(() => {
    if (user) {
      setFormData((prevData) =>
        prevData.map((item) => {
          switch (item.id) {
            case "firstname":
              return { ...item, value: user.firstName || "" };
            case "lastname":
              return { ...item, value: user.lastName || "" };
            case "username":
              return { ...item, value: user.username || "" };
            case "email":
              return { ...item, value: user.email || "" };
            case "password":
              return { ...item, value: "" }; // Assuming password is not set from user data
            default:
              return item;
          }
        }),
      );

      setImage(
        user?.profileAvatar?.avatarUrl ? user?.profileAvatar?.avatarUrl : null,
      );
    }
  }, [user]);

  useEffect(() => {
    setUserData();
  }, [setUserData]);

  const handleChange = (newValue: string, id: TypeIdGeneral) => {
    setFormData((prevData: IInputGeneral[]) =>
      prevData.map((item) =>
        item.id === id ? { ...item, value: newValue } : item,
      ),
    );
  };

  async function patchUser() {
    setIsUpdating(true); // Устанавливаем флаг начала обновления

    let newData: IRequestPatchUser = {
      firstName:
        formData.find((val) => val.id === "firstname")?.value || undefined,
      lastName:
        formData.find((val) => val.id === "lastname")?.value || undefined,
      username:
        formData.find((val) => val.id === "username")?.value || undefined,
      email: formData.find((val) => val.id === "email")?.value || undefined,
    };

    if (image) {
      newData = {
        ...newData,
        profileAvatar: {
          avatarUrl: image,
        },
      };
    } else if (image === null && user?.profileAvatar?.avatarUrl) {
      await deleteAvatarClient();
    }

    try {
      await dispatch(updateUser(newData)).unwrap();
    } catch (error) {
      console.error("Fehler beim Aktualisieren des Profils:", error);
    } finally {
      setIsUpdating(false); // Сбрасываем флаг завершения обновления
    }
  }

  if (isUpdating || loading || !user) {
    return (
      <div className="loader-container">
        <Loader size={100} borderWidth={7} />
      </div>
    );
  }

  return (
    <div className={styles.profileBlock}>
      <div className={styles.menu}>
        <MenuTabs type="client" />
      </div>
      {router.pathname === "/profile" ? (
        <>
          <div className={styles.Verification}>
            {user && (
              <span className={styles.Verification_text}>
                Registriert am: {formatDate(user?.createdAt)}
              </span>
            )}
          </div>
          <div className={styles.blockInput}>
            <form className={styles.container}>
              <AvatarCircle
                image={image}
                setImage={setImage}
                handleImageRemove={handleImageRemove}
              />
              {formData.map((item) => {
                if (item.type === "password") {
                  <InputPassword
                    id={String(item.id)}
                    label={item.label}
                    type={item.type}
                    value={item.value}
                    className={styles.inputField}
                    onChange={(e) => handleChange(e.target.value, item.id)}
                    placeholder="Enter password"
                  />;
                } else {
                  return (
                    <Input
                      id={String(item.id)}
                      label={item.label}
                      type={item.type}
                      value={item.value}
                      className={styles.inputField}
                      onChange={(e) => handleChange(e.target.value, item.id)}
                      placeholder={item.placeholder}
                    />
                  );
                }
              })}
            </form>
            <div className={styles.ButtonBlock}>
              <div className={styles.Button}>
                <Button onClick={patchUser}>Edite</Button>
              </div>
            </div>{" "}
          </div>
        </>
      ) : (
        <Chat />
      )}
    </div>
  );
};
